import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Radio,
  Table,
  Tag,
  Space,
  Modal,
  Row,
  Col,
  Typography,
  message,
  Tooltip,
  Popconfirm,
  Badge,
  Alert,
  Checkbox,
  Popover,
  Divider,
} from 'antd';
import {
  UserAddOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  LockOutlined,
  SafetyOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  KeyOutlined,
  ReloadOutlined,
  CalendarOutlined,
  LoginOutlined,
  AppstoreOutlined,
  ReadOutlined,
  FormOutlined,
  RollbackOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  DownOutlined,
  UpOutlined,
  LockFilled,
  UnlockFilled,
} from '@ant-design/icons';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../adminpages/AdminUserManagement.css';
import axiosConfig from '../../../../../config/axiosConfig';
import { useUserContext, canWrite as canWritePage } from '../../../../../context/userContext';

const { Text } = Typography;
const { Option } = Select;

// ── Skeleton shimmer constants ────────────────────────────────────────────────
const SKEL_GRAD = 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)';
const SKEL_GRAD_DARK = 'linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%)';
const SKEL_ANIM = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ? 'none'
  : 'skeleton-loading 1s infinite ease-in-out';

const TeamTableSkeleton = React.memo(() => (
  <div style={{ padding: '0 0 8px' }}>
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8 }}>
      <div style={{ padding: '12px 16px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 16 }}>
        {[80, 120, 100, 160, 60, 80].map((w, i) => (
          <div key={i} style={{ height: 16, background: SKEL_GRAD_DARK, borderRadius: 4, width: w, flexShrink: 0, animation: SKEL_ANIM }} />
        ))}
      </div>
      {[0, 1, 2, 3, 4].map((r) => (
        <div key={r} style={{ padding: '14px 16px', borderBottom: r < 4 ? '1px solid #f0f0f0' : 'none', display: 'flex', gap: 16, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: 80, flexShrink: 0 }}>
            <div style={{ width: 38, height: 38, borderRadius: '50%', background: SKEL_GRAD, animation: SKEL_ANIM, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ height: 14, background: SKEL_GRAD, borderRadius: 4, width: '100%', marginBottom: 6, animation: SKEL_ANIM }} />
              <div style={{ height: 10, background: SKEL_GRAD_DARK, borderRadius: 4, width: '70%', animation: SKEL_ANIM }} />
            </div>
          </div>
          {[120, 100, 160, 60, 80].map((w, c) => (
            <div key={c} style={{ height: 16, background: SKEL_GRAD, borderRadius: 4, width: w, flexShrink: 0, animation: SKEL_ANIM }} />
          ))}
        </div>
      ))}
    </div>
    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' }}>
      <div style={{ height: 20, background: SKEL_GRAD, borderRadius: 4, width: 140, animation: SKEL_ANIM }} />
      <div style={{ height: 28, background: SKEL_GRAD, borderRadius: 6, width: 180, animation: SKEL_ANIM }} />
    </div>
  </div>
));

// Page keys that can be assigned to sub-admins
// Hardcoded fallback — will be overridden by `available_pages` from the API context
const AVAILABLE_PAGES_FALLBACK = [
  { key: 'dashboard',   label: 'Dashboard',           icon: '📊', group: 'core' },
  { key: 'campaigns',   label: 'Campaigns',           icon: '📢', group: 'whatsapp' },
  { key: 'templates',   label: 'Templates',           icon: '📝', group: 'whatsapp' },
  { key: 'inbox',       label: 'Inbox',               icon: '💬', group: 'whatsapp' },
  { key: 'contacts',    label: 'Contacts',            icon: '👥', group: 'whatsapp' },
  { key: 'wallet',      label: 'Wallet',              icon: '💰', group: 'settings' },
  { key: 'admin',       label: 'Admin Management',    icon: '🔐', group: 'settings' },
  { key: 'audit-logs',  label: 'Audit Logs',          icon: '📋', group: 'settings' },
];

// Human-friendly descriptions shown in the Role info popover
const ROLE_DESCRIPTIONS = {
  'Super Admin': {
    summary: 'Full access to all pages (WhatsApp + Email) with read & write permissions.',
    capabilities: [
      { page: 'Dashboard', perm: 'write' },
      { page: 'Campaigns', perm: 'write' },
      { page: 'Templates', perm: 'write' },
      { page: 'Inbox', perm: 'write' },
      { page: 'Contacts', perm: 'write' },
      { page: 'Wallet', perm: 'write' },
      { page: 'Admin Management', perm: 'write' },
      { page: 'Audit Logs', perm: 'write' },
      { page: 'Email Dashboard', perm: 'write' },
      { page: 'Email Campaigns', perm: 'write' },
      { page: 'Email Templates', perm: 'write' },
      { page: 'Email Settings', perm: 'write' },
      { page: 'Sender Profile', perm: 'write' },
    ],
  },
  'Marketing Admin': {
    summary: 'Manages campaigns, templates, contacts and inbox across WhatsApp & Email. No financial or admin access.',
    capabilities: [
      { page: 'Dashboard', perm: 'write' },
      { page: 'Campaigns', perm: 'write' },
      { page: 'Templates', perm: 'write' },
      { page: 'Inbox', perm: 'write' },
      { page: 'Contacts', perm: 'write' },
      { page: 'Email Dashboard', perm: 'write' },
      { page: 'Email Campaigns', perm: 'write' },
      { page: 'Email Templates', perm: 'write' },
    ],
  },
  'Operations Admin': {
    summary: 'Handles day-to-day operations — campaigns, inbox and contacts across WhatsApp & Email.',
    capabilities: [
      { page: 'Dashboard', perm: 'write' },
      { page: 'Campaigns', perm: 'write' },
      { page: 'Inbox', perm: 'write' },
      { page: 'Contacts', perm: 'write' },
      { page: 'Email Dashboard', perm: 'write' },
      { page: 'Email Campaigns', perm: 'write' },
    ],
  },
  'Finance Admin': {
    summary: 'Manages wallet & billing. Dashboard access only (view-only email dashboard).',
    capabilities: [
      { page: 'Dashboard', perm: 'write' },
      { page: 'Wallet', perm: 'write' },
      { page: 'Email Dashboard', perm: 'read' },
    ],
  },
  'Support Admin': {
    summary: 'Handles customer conversations and contacts. View-only access to templates and email dashboard.',
    capabilities: [
      { page: 'Dashboard', perm: 'write' },
      { page: 'Inbox', perm: 'write' },
      { page: 'Contacts', perm: 'write' },
      { page: 'Templates', perm: 'read' },
      { page: 'Email Dashboard', perm: 'read' },
      { page: 'Email Templates', perm: 'read' },
    ],
  },
};

// Default pages + permissions for each role
// NOTE: includes email-module equivalents so roles auto-select correctly
// when the backend returns email pages in `available_pages`.
// getRoleDefaults() filters these against the actually-available pages at runtime,
// so if the email module is not enabled, email keys are silently dropped.
const ROLE_DEFAULT_PAGES = {
  'Super Admin': {
    // Static fallback — getRoleDefaults() overrides this dynamically to select ALL available pages
    pages: AVAILABLE_PAGES_FALLBACK.map(p => p.key),
    perms: Object.fromEntries(AVAILABLE_PAGES_FALLBACK.map(p => [p.key, 'write'])),
  },
  'Marketing Admin': {
    pages: [
      'dashboard',
      'campaigns', 'templates', 'inbox', 'contacts',
      'email_dashboard', 'email_campaigns', 'email_templates',
    ],
    perms: {
      dashboard: 'write',
      campaigns: 'write', templates: 'write', inbox: 'write', contacts: 'write',
      email_dashboard: 'write', email_campaigns: 'write', email_templates: 'write',
    },
  },
  'Operations Admin': {
    pages: [
      'dashboard',
      'campaigns', 'inbox', 'contacts',
      'email_dashboard', 'email_campaigns',
    ],
    perms: {
      dashboard: 'write',
      campaigns: 'write', inbox: 'write', contacts: 'write',
      email_dashboard: 'write', email_campaigns: 'write',
    },
  },
  'Finance Admin': {
    pages: [
      'dashboard',
      'wallet',
      'email_dashboard',
    ],
    perms: {
      dashboard: 'write',
      wallet: 'write',
      email_dashboard: 'read',
    },
  },
  'Support Admin': {
    pages: [
      'dashboard',
      'inbox', 'contacts', 'templates',
      'email_dashboard', 'email_templates',
    ],
    perms: {
      dashboard: 'write',
      inbox: 'write', contacts: 'write', templates: 'read',
      email_dashboard: 'read', email_templates: 'read',
    },
  },
};

const ClientAdminManagement = () => {
  const [form] = Form.useForm();
  const { userCtx } = useUserContext();
  const adminCanWrite = !userCtx || !userCtx.pages || canWritePage(userCtx, 'admin');

  // Dynamic AVAILABLE_PAGES: ONLY use API-provided available_pages when the backend
  // explicitly returns them. Otherwise fall back to the local hardcoded list that has
  // always been used for the permissions form.
  const apiAvailablePages = userCtx?.available_pages;
  const AVAILABLE_PAGES = (apiAvailablePages && Array.isArray(apiAvailablePages) && apiAvailablePages.length > 0)
    ? apiAvailablePages.map(p => ({ key: p.key, label: p.label, group: p.group || 'core', icon: AVAILABLE_PAGES_FALLBACK.find(f => f.key === p.key)?.icon || '📄' }))
    : AVAILABLE_PAGES_FALLBACK;

  // Group pages by their `group` field for a grouped permissions UI
  const pageGroups = AVAILABLE_PAGES.reduce((acc, p) => {
    const g = p.group || 'core';
    if (!acc[g]) acc[g] = [];
    acc[g].push(p);
    return acc;
  }, {});

  const [loading, setLoading] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);

  const [contactMethod, setContactMethod] = useState('email');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [showPassword, setShowPassword] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Controls whether the Advanced Access (page permissions) section is visible
  const [showAdvancedAccess, setShowAdvancedAccess] = useState(false);

  // Pages & permissions for create form
  const [selectedPages, setSelectedPages] = useState([]);
  const [pagePermissions, setPagePermissions] = useState({});

  // Edit Permissions modal state
  const [editPermUser, setEditPermUser] = useState(null);
  const [editPermPages, setEditPermPages] = useState([]);
  const [editPermPerms, setEditPermPerms] = useState({});
  const [editPermRole, setEditPermRole] = useState(null);
  const [editPermLoading, setEditPermLoading] = useState(false);

  // Restore deactivated user modal state
  const [restoreModal, setRestoreModal] = useState({ visible: false, users: [], pendingFormData: null, loading: false });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const roleColorMap = {
    'Super Admin': 'red',
    'Operations Admin': 'orange',
    'Support Admin': 'cyan',
    'Finance Admin': 'gold',
    'Marketing Admin': 'purple',
  };

  useEffect(() => {
    fetchRoles();
    fetchAdminUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch roles from client API ──────────────────────────────────────────
  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const response = await axiosConfig.get('/admin/client/roles');
      // API returns { status, statuscode, entity: [...] } or { success, data: [...] }
      const isSuccess = response.data?.status === true || response.data?.statuscode === 200 || response.data?.success;
      const rawList = response.data?.entity || response.data?.data;
      if (isSuccess && Array.isArray(rawList)) {
        const roles = rawList
          .map(role => ({
            value: role.roleid,
            label: role.rolename,
            color: roleColorMap[role.rolename] || 'default',
          }))
          .filter(role => role.value && role.label);
        setRoleOptions(roles);
      } else {
        setRoleOptions([]);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      message.error('Failed to fetch roles');
    } finally {
      setRolesLoading(false);
    }
  };

  // ── Fetch sub-admin users ────────────────────────────────────────────────
  const fetchAdminUsers = async (page = pagination.page, search = searchText) => {
    setTableLoading(true);
    try {
      const response = await axiosConfig.post('/admin/client/users', {
        page,
        pageSize: pagination.pageSize,
        search,
      });

      const isSuccess = response.data?.status === true || response.data?.statuscode === 200 || response.data?.statuscode === 201;
      if (isSuccess) {
        const data = response.data?.entity;
        const users = (data.users || []).map(user => {
          const cc = user.countrycode
            ? (user.countrycode.startsWith('+') ? user.countrycode : `+${user.countrycode}`)
            : null;
          return {
            id: user.companyid,
            name: user.companyname,
            email: user.emailid || null,
            mobile: cc && user.contactno ? `${cc}${user.contactno}` : null,
            countrycode: cc,
            contactno: user.contactno,
            role: user.roleid,
            rolename: user.rolename || user.company_type,
            status: user.status === 'Active' ? 'active' : 'inactive',
            createdAt: user.entrytime ? new Date(user.entrytime).toLocaleDateString() : 'N/A',
            lastLogin: (user.lastlogintime || user.last_login || user.entrytime)
              ? new Date(user.lastlogintime || user.last_login || user.entrytime).toLocaleDateString()
              : 'Never',
            company_type: user.company_type,
            pages: user.pages || null,
            permissions: user.permissions || null,
            parent_company_id: user.parent_company_id || null,
          };
        });
        setAdminUsers(users);
        setPagination({
          page: data.pagination?.page || page,
          pageSize: data.pagination?.pageSize || pagination.pageSize,
          total: data.pagination?.total || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
      if (error.response?.status === 403) {
        message.error('Access denied');
      } else {
        message.error(error.response?.data?.message || 'Failed to fetch team members');
      }
    } finally {
      setTableLoading(false);
    }
  };

  // ── Generate random password ─────────────────────────────────────────────
  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
    password += '0123456789'[Math.floor(Math.random() * 10)];
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)];
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleContactMethodChange = (e) => {
    const method = e.target.value;
    setContactMethod(method);
    if (method === 'email') {
      form.setFieldsValue({ mobile: undefined, countrycode: undefined });
      setPhoneNumber('');
    } else {
      form.setFieldsValue({ email: undefined });
    }
  };

  const handlePhoneChange = (value, country) => {
    const dialCode = country.dialCode;
    setPhoneNumber(value);
    const code = `+${dialCode}`;
    setCountryCode(code);
    form.setFieldsValue({
      mobile: value.replace(code.replace('+', ''), ''),
      countrycode: code,
    });
  };

  const handleGeneratePassword = () => {
    const generated = generatePassword();
    form.setFieldsValue({ password: generated, confirmPassword: generated });
    setShowPassword(true);
  };

  // ── Create sub-admin ────────────────────────────────────────────────────
  const handleSubmit = async (values) => {
    setLoading(true);
    // Hoist requestBody so catch block can access it for restore modal
    let requestBody = null;
    try {
      const userPassword = values.password;
      requestBody = {
        companyname: values.name,
        password: userPassword,
        company_type: roleOptions.find(r => r.value === values.role)?.label || 'Support Admin',
        roleid: values.role,
        pages: selectedPages.length > 0 ? selectedPages : null,
        permissions: Object.keys(pagePermissions).length > 0 ? pagePermissions : null,
        colour_theme: 'light',
        display_mode: 'default',
      };

      if (contactMethod === 'email') {
        requestBody.emailid = values.email;
      } else {
        requestBody.emailid = '';
        requestBody.countrycode = countryCode;
        requestBody.contactno = values.mobile;
      }

      const response = await axiosConfig.post('/admin/client/create-user', requestBody);

      const isSuccess = response.data?.status === true || response.data?.statuscode === 200 || response.data?.statuscode === 201;
      if (isSuccess) {
        const createdUser = response.data?.entity;
        const username = contactMethod === 'email'
          ? createdUser.emailid
          : `${createdUser.countrycode || countryCode}${createdUser.contactno || values.mobile}`;

        setCreatedCredentials({
          name: createdUser.companyname,
          email: createdUser.emailid || null,
          mobile: createdUser.contactno ? `${createdUser.countrycode || countryCode}${createdUser.contactno}` : null,
          role: values.role,
          rolename: createdUser.rolename || createdUser.company_type || 'Unknown',
          password: userPassword,
          username: username,
          pages: createdUser.pages || selectedPages,
          permissions: createdUser.permissions || pagePermissions,
        });

        setShowCredentialsModal(true);
        setShowCreateModal(false);
        form.resetFields();
        setPhoneNumber('');
        setCountryCode('+91');
        setContactMethod('email');
        setShowPassword(false);
        setSelectedPages([]);
        setPagePermissions({});
        message.success(response.data?.message || 'Team member created successfully!');
        fetchAdminUsers();
      } else {
        message.error(response.data?.message || 'Failed to create team member');
      }
    } catch (error) {
      console.error('Error creating admin user:', error);
      const status = error.response?.status;
      const errData = error.response?.data;

      // ── 409: Deactivated user with same credentials ───────────────────
      if (status === 409 && errData?.code === 'DEACTIVATED_USER_EXISTS') {
        setRestoreModal({
          visible: true,
          users: errData.deactivated_users || [],
          pendingFormData: requestBody,
          loading: false,
        });
        return;
      }

      let errorMessage = errData?.message || 'Failed to create team member';
      if (status === 400) {
        if (errorMessage.includes('email')) errorMessage = 'Email already registered';
        else if (errorMessage.includes('mobile') || errorMessage.includes('contact')) errorMessage = 'Mobile number already registered';
      } else if (status === 403) {
        errorMessage = 'Access denied: You do not have permission to create team members';
      }
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ── Restore a previously deactivated team member ──────────────────────────
  const handleRestore = async () => {
    const { users, pendingFormData } = restoreModal;
    if (!users.length || !pendingFormData) return;
    const targetUser = users[0];
    setRestoreModal(prev => ({ ...prev, loading: true }));
    try {
      const response = await axiosConfig.post(`/admin/client/users/${targetUser.companyid}/restore`, {
        companyname: pendingFormData.companyname,
        password: pendingFormData.password,
        company_type: pendingFormData.company_type,
        roleid: pendingFormData.roleid,
        pages: pendingFormData.pages,
        permissions: pendingFormData.permissions,
      });
      const isSuccess = response.data?.status === true || response.data?.statuscode === 200 || response.data?.statuscode === 201;
      if (isSuccess) {
        const restoredUser = response.data?.entity || {};
        const username = pendingFormData.emailid || `${pendingFormData.countrycode || ''}${pendingFormData.contactno || ''}`;
        setCreatedCredentials({
          name: restoredUser.companyname || pendingFormData.companyname,
          email: restoredUser.emailid || pendingFormData.emailid || null,
          mobile: restoredUser.contactno ? `${restoredUser.countrycode || pendingFormData.countrycode || ''}${restoredUser.contactno}` : null,
          role: pendingFormData.roleid,
          rolename: restoredUser.rolename || restoredUser.company_type || pendingFormData.company_type || 'Admin',
          password: pendingFormData.password,
          username,
          pages: restoredUser.pages || pendingFormData.pages,
          permissions: restoredUser.permissions || pendingFormData.permissions,
          restored: true,
        });
        setRestoreModal({ visible: false, users: [], pendingFormData: null, loading: false });
        setShowCreateModal(false);
        form.resetFields();
        setPhoneNumber(''); setCountryCode('+91'); setContactMethod('email'); setShowPassword(false);
        setSelectedPages([]); setPagePermissions({});
        setShowCredentialsModal(true);
        message.success(response.data?.message || 'Team member restored successfully!');
        fetchAdminUsers();
      } else {
        message.error(response.data?.message || 'Failed to restore team member');
        setRestoreModal(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to restore team member');
      setRestoreModal(prev => ({ ...prev, loading: false }));
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard!`);
  };

  // ── Status toggle (deactivate / reactivate) ─────────────────────────────
  const handleStatusToggle = async (record) => {
    try {
      let response;
      if (record.status === 'active') {
        response = await axiosConfig.delete(`/admin/client/users/${record.id}`);
      } else {
        response = await axiosConfig.post(`/admin/client/users/${record.id}/reactivate`);
      }
      const isSuccess = response.data?.status === true || response.data?.statuscode === 200 || response.data?.statuscode === 201;
      if (isSuccess) {
        message.success(response.data?.message || 'Status updated successfully');
        fetchAdminUsers();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      message.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  // ── Delete (deactivate) ─────────────────────────────────────────────────
  const handleDelete = async (record) => {
    try {
      const response = await axiosConfig.delete(`/admin/client/users/${record.id}`);
      const isSuccess = response.data?.status === true || response.data?.statuscode === 200 || response.data?.statuscode === 201;
      if (isSuccess) {
        message.success(response.data?.message || 'Team member deactivated successfully');
        fetchAdminUsers();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error(error.response?.data?.message || 'Failed to deactivate team member');
    }
  };

  // ── Role update ─────────────────────────────────────────────────────────
  const handleRoleUpdate = async (userId, newRoleId) => {
    try {
      const response = await axiosConfig.put(`/admin/client/users/${userId}/role`, {
        roleid: newRoleId,
      });
      const isSuccess = response.data?.status === true || response.data?.statuscode === 200 || response.data?.statuscode === 201;
      if (isSuccess) {
        message.success(response.data?.message || 'Role updated successfully');
        fetchAdminUsers();
      } else {
        message.error(response.data?.message || 'Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      message.error(error.response?.data?.message || 'Failed to update role');
    }
  };

  // ── Edit Permissions ────────────────────────────────────────────────────
  const openEditPermissions = (record) => {
    setEditPermUser(record);
    setEditPermPages(record.pages || []);
    setEditPermPerms(record.permissions || {});
    setEditPermRole(record.role);
  };

  const handleSavePermissions = async () => {
    if (!editPermUser) return;
    setEditPermLoading(true);
    try {
      const response = await axiosConfig.put(`/admin/client/update-permissions/${editPermUser.id}`, {
        pages: editPermPages.length > 0 ? editPermPages : null,
        permissions: Object.keys(editPermPerms).length > 0 ? editPermPerms : null,
        roleid: editPermRole,
      });
      const isSuccess = response.data?.status === true || response.data?.statuscode === 200 || response.data?.statuscode === 201;
      if (isSuccess) {
        message.success(response.data?.message || 'Permissions updated successfully');
        setEditPermUser(null);
        fetchAdminUsers();
      } else {
        message.error(response.data?.message || 'Failed to update permissions');
      }
    } catch (error) {
      console.error('Error updating permissions:', error);
      message.error(error.response?.data?.message || 'Failed to update permissions');
    } finally {
      setEditPermLoading(false);
    }
  };

  // ── Modal helpers ───────────────────────────────────────────────────────
  const handleOpenCreateModal = () => {
    form.resetFields();
    setPhoneNumber('');
    setCountryCode('+91');
    setContactMethod('email');
    setShowPassword(false);
    setSelectedPages([]);
    setPagePermissions({});
    setShowAdvancedAccess(false);
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    form.resetFields();
    setPhoneNumber('');
    setCountryCode('+91');
    setContactMethod('email');
    setShowPassword(false);
    setSelectedPages([]);
    setPagePermissions({});
    setShowAdvancedAccess(false);
  };

  // Resolve role defaults — used by both Create and Edit modals.
  // Filters against AVAILABLE_PAGES so that keys for disabled modules
  // (e.g. email pages when email isn't enabled) are silently dropped.
  const getRoleDefaults = (roleName) => {
    const availableKeys = new Set(AVAILABLE_PAGES.map(p => p.key));

    if (roleName === 'Super Admin') {
      // Super Admin gets ALL available pages with write access
      const allKeys = AVAILABLE_PAGES.map(p => p.key);
      return { pages: allKeys, perms: Object.fromEntries(allKeys.map(k => [k, 'write'])) };
    }

    const defaults = ROLE_DEFAULT_PAGES[roleName];
    if (defaults) {
      // Only include pages that actually exist in the current AVAILABLE_PAGES
      const filteredPages = defaults.pages.filter(k => availableKeys.has(k));
      const filteredPerms = {};
      filteredPages.forEach(k => { filteredPerms[k] = defaults.perms[k] || 'write'; });
      return { pages: filteredPages, perms: filteredPerms };
    }

    return null;
  };

  const handleRoleChange = (roleId) => {
    const roleName = roleOptions.find(r => r.value === roleId)?.label || '';
    const defaults = getRoleDefaults(roleName);
    if (defaults) {
      setSelectedPages(defaults.pages);
      setPagePermissions(defaults.perms);
    }
  };

  // Role change handler for Edit Permissions modal — also auto-applies defaults
  const handleEditPermRoleChange = (roleId) => {
    setEditPermRole(roleId);
    const roleName = roleOptions.find(r => r.value === roleId)?.label || '';
    const defaults = getRoleDefaults(roleName);
    if (defaults) {
      setEditPermPages(defaults.pages);
      setEditPermPerms(defaults.perms);
    }
  };

  const handleNameInput = (e) => {
    let value = e.target.value;
    value = value.replace(/[^a-zA-Z\s]/g, '');
    value = value.replace(/\b\w/g, char => char.toUpperCase());
    form.setFieldsValue({ name: value });
  };

  // ── Table columns ───────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Team Member',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: '50%',
            background: `linear-gradient(135deg, ${roleColorMap[record.rolename] || '#0036b0'}, #1677ff)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 15, flexShrink: 0,
          }}>
            {text?.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>{text}</div>
            <div style={{ fontSize: 11, color: '#999' }}>Added {record.createdAt}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          {record.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <MailOutlined style={{ color: '#1890ff', fontSize: 12 }} />
              <span style={{ fontSize: 13 }}>{record.email}</span>
            </div>
          )}
          {record.mobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <PhoneOutlined style={{ color: '#52c41a', fontSize: 12 }} />
              <span style={{ fontSize: 13 }}>{record.mobile}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'rolename',
      key: 'role',
      render: (rolename, record) => (
        <Select
          value={record.role}
          onChange={(newRoleId) => handleRoleUpdate(record.id, newRoleId)}
          style={{ width: 160 }}
          size="small"
          disabled={!adminCanWrite}
        >
          {roleOptions.map(role => (
            <Option key={role.value} value={role.value}>
              {role.label}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Access',
      dataIndex: 'pages',
      key: 'pages',
      width: 200,
      render: (pages) => {
        if (!pages || pages.length === 0) {
          return <Tag style={{ margin: 0, borderRadius: 6, fontSize: 11, borderColor: '#e2e8f0', color: '#475569', background: '#f8fafc' }}>Full Access</Tag>;
        }
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {pages.slice(0, 3).map(p => (
              <Tag key={p} style={{ fontSize: 10.5, margin: 0, borderRadius: 6, borderColor: '#e2e8f0', color: '#334155', background: '#f8fafc' }}>
                {AVAILABLE_PAGES.find(ap => ap.key === p)?.label || p}
              </Tag>
            ))}
            {pages.length > 3 && (
              <Tooltip title={pages.slice(3).map(p => AVAILABLE_PAGES.find(ap => ap.key === p)?.label || p).join(', ')}>
                <Tag style={{ fontSize: 10.5, margin: 0, borderRadius: 6, borderColor: '#dbeafe', color: '#1d4ed8', background: '#eff6ff' }}>+{pages.length - 3}</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Badge
          status={status === 'active' ? 'success' : 'error'}
          text={
            <span style={{ color: status === 'active' ? '#52c41a' : '#ff4d4f', fontWeight: 500, fontSize: 13 }}>
              {status === 'active' ? 'Active' : 'Inactive'}
            </span>
          }
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: windowWidth >= 768 ? false : 'right',
      onHeaderCell: () => ({ style: { background: '#fafafa', boxShadow: windowWidth < 768 ? '-4px 0 8px -2px rgba(0,0,0,0.08)' : 'none' } }),
      onCell: () => ({ style: { background: '#fff', boxShadow: windowWidth < 768 ? '-4px 0 8px -2px rgba(0,0,0,0.06)' : 'none' } }),
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button type="text" icon={<EyeOutlined />} size="small" onClick={() => showUserDetails(record)} />
          </Tooltip>
          {adminCanWrite && record.parent_company_id && (
            <Tooltip title="Edit Permissions">
              <Button type="text" icon={<EditOutlined style={{ color: '#1890ff' }} />} size="small" onClick={() => openEditPermissions(record)} />
            </Tooltip>
          )}
          {adminCanWrite && (
            <Tooltip title={record.status === 'active' ? 'Deactivate' : 'Activate'}>
              <Button
                type="text"
                icon={record.status === 'active' ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                size="small"
                style={{ color: record.status === 'active' ? '#ff4d4f' : '#52c41a' }}
                onClick={() => handleStatusToggle(record)}
              />
            </Tooltip>
          )}
          {adminCanWrite && (
            <Popconfirm
              title="Deactivate this team member?"
              description="They will lose access until reactivated."
              onConfirm={() => handleDelete(record)}
              okText="Yes, Deactivate"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Remove">
                <Button type="text" icon={<DeleteOutlined />} size="small" danger />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // ── User detail modal ─────────────────────────────────────────────────
  const showUserDetails = (record) => {
    const permissionIcon = (perm) => perm === 'write'
      ? <FormOutlined style={{ color: '#1890ff', fontSize: 13 }} />
      : <ReadOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />;
    const permissionColor = (perm) => perm === 'write' ? '#e6f4ff' : '#fafafa';
    const permissionTagColor = (perm) => perm === 'write' ? 'blue' : 'default';
    const permissionBorder = (perm) => perm === 'write' ? '#91caff' : '#e8e8e8';

    Modal.info({
      title: null,
      icon: null,
      width: windowWidth < 576 ? '92%' : windowWidth < 992 ? 600 : 700,
      bodyStyle: { overflow: 'visible', padding: windowWidth < 576 ? '12px 14px 6px' : '14px 18px 8px' },
      wrapClassName: 'admin-user-details-modal',
      style: { top: windowWidth < 576 ? 18 : 30 },
      content: (
        <div>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
            paddingBottom: 12, borderBottom: '1px solid #f0f0f0',
          }}>
            <div style={{
              width: windowWidth < 576 ? 40 : 46, height: windowWidth < 576 ? 40 : 46, borderRadius: '50%', flexShrink: 0,
              background: `linear-gradient(135deg, ${roleColorMap[record.rolename] || '#0036b0'}, #1677ff)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: windowWidth < 576 ? 15 : 17,
            }}>
              {record.name?.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: windowWidth < 576 ? 15 : 16, fontWeight: 600, color: '#1a1a1a' }}>{record.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                <Tag color={roleColorMap[record.rolename] || 'default'} style={{ margin: 0 }}>{record.rolename || 'N/A'}</Tag>
                <Badge
                  status={record.status === 'active' ? 'success' : 'error'}
                  text={<span style={{ fontSize: 12, color: record.status === 'active' ? '#52c41a' : '#ff4d4f', fontWeight: 500 }}>
                    {record.status === 'active' ? 'Active' : 'Inactive'}
                  </span>}
                />
              </div>
            </div>
          </div>

          {/* Info Grid */}
          <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
            {[
              { icon: <MailOutlined style={{ color: '#1890ff', fontSize: 12 }} />, label: 'Email', value: record.email || 'N/A' },
              { icon: <PhoneOutlined style={{ color: '#52c41a', fontSize: 12 }} />, label: 'Mobile', value: record.mobile || 'N/A' },
              { icon: <CalendarOutlined style={{ color: '#faad14', fontSize: 12 }} />, label: 'Created', value: record.createdAt },
              { icon: <LoginOutlined style={{ color: '#722ed1', fontSize: 12 }} />, label: 'Last Login', value: record.lastLogin || 'Never' },
            ].map((item, idx) => (
              <Col span={windowWidth < 576 ? 24 : 12} key={idx}>
                <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    {item.icon}
                    <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</Text>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#1a1a1a', wordBreak: 'break-all' }}>{item.value}</div>
                </div>
              </Col>
            ))}
          </Row>

          {/* Page Permissions Section */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <AppstoreOutlined style={{ color: '#1890ff', fontSize: 15 }} />
              <span style={{ fontSize: 14, fontWeight: 600 }}>Page Access & Permissions</span>
            </div>
            {(!record.pages || record.pages.length === 0) ? (
              <div style={{
                background: 'linear-gradient(135deg, #fffbe6, #fff7e6)',
                border: '1px solid #ffe58f',
                borderRadius: 10, padding: '14px 16px',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <SafetyOutlined style={{ color: '#faad14', fontSize: 18 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#ad6800' }}>Full Access</div>
                  <div style={{ fontSize: 12, color: '#ad8b00' }}>This member has unrestricted access to all pages</div>
                </div>
              </div>
            ) : (
              <div style={{ border: '1px solid #e8e8e8', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: windowWidth < 576 ? '1fr' : '1fr 1fr', background: '#fafafa',
                  padding: '8px 14px', borderBottom: '1px solid #e8e8e8',
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.5 }}>Page &amp; Permission</span>
                  {windowWidth >= 576 && <span style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.5 }}>Page &amp; Permission</span>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: windowWidth < 576 ? '1fr' : '1fr 1fr', gap: 1, background: '#f0f0f0' }}>
                  {record.pages.map((pageKey) => {
                    const perm = record.permissions?.[pageKey] || 'read';
                    const pageLabel = AVAILABLE_PAGES.find(ap => ap.key === pageKey)?.label || pageKey;
                    return (
                      <div key={pageKey} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '9px 12px', background: permissionColor(perm), gap: 6,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                          {permissionIcon(perm)}
                          <span style={{ fontSize: 12, fontWeight: 500, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pageLabel}</span>
                        </div>
                        <Tag
                          color={permissionTagColor(perm)}
                          style={{
                            margin: 0, fontSize: 10, borderRadius: 4,
                            border: `1px solid ${permissionBorder(perm)}`, padding: '0 5px', flexShrink: 0,
                          }}
                        >
                          {perm === 'write' ? 'R/W' : 'Read'}
                        </Tag>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      ),
      okText: 'Close',
      okButtonProps: { style: { borderRadius: 6 } },
    });
  };

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div>
      {/* Toolbar */}
      <div className="cam-toolbar">
        <div className="cam-toolbar-title">
          <TeamOutlined style={{ color: '#0036b0', fontSize: 20 }} />
          <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Team Members</span>
          <Badge count={pagination.total} style={{ backgroundColor: '#0036b0' }} />
        </div>
        <div className="cam-toolbar-actions">
          <Input.Search
            placeholder="Search members..."
            allowClear
            className="cam-toolbar-search"
            style={{ width: windowWidth < 576 ? '100%' : 220 }}
            onSearch={(value) => {
              setSearchText(value);
              setPagination(prev => ({ ...prev, page: 1 }));
              fetchAdminUsers(1, value);
            }}
          />
          <div className="cam-toolbar-actions-row">
            <Button className="cam-refresh-btn" icon={<ReloadOutlined />} onClick={() => fetchAdminUsers()} loading={tableLoading}>
              Refresh
            </Button>
            {adminCanWrite && (
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={handleOpenCreateModal}
                className="cam-add-btn"
                style={{ background: '#0036b0', borderColor: '#0036b0', borderRadius: 8, fontWeight: 600 }}
              >
                Add Team Member
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Team Members Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
        bodyStyle={{ padding: tableLoading && adminUsers.length === 0 ? '12px 16px' : 0 }}
      >
        {tableLoading && adminUsers.length === 0 ? (
          <TeamTableSkeleton />
        ) : (
        <Table
          dataSource={adminUsers}
          columns={columns}
          loading={tableLoading && adminUsers.length > 0}
          rowKey="id"
          size="small"
          scroll={{ x: 900 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: windowWidth >= 768,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} members`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, page, pageSize }));
              fetchAdminUsers(page, searchText);
            },
            simple: windowWidth < 576,
          }}
        />
        )}
      </Card>

      {/* ── Create Team Member Modal ──────────────────────────────────────── */}
      {adminCanWrite && (
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'linear-gradient(135deg, #0036b0, #1677ff)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <UserAddOutlined style={{ color: '#fff', fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>Add Team Member</div>
                <div style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>Create a new sub-admin for your organization</div>
              </div>
            </div>
          }
          open={showCreateModal}
          onCancel={handleCloseCreateModal}
          footer={null}
          width={windowWidth < 576 ? '98%' : windowWidth < 768 ? '95%' : windowWidth < 1200 ? 860 : 960}
          bodyStyle={{ overflow: 'visible', padding: '12px 24px 8px' }}
          wrapClassName="admin-user-details-modal"
          style={{ top: 24 }}
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
            <Row gutter={[24, 0]}>
              {/* Left column: identity + credentials */}
              <Col xs={24} md={12}>
                <Form.Item name="name" label="Full Name" style={{ marginBottom: 12 }}
                  rules={[
                    { required: true, message: 'Please enter full name' },
                    { min: 3, message: 'Name must be at least 3 characters' },
                    { max: 50, message: 'Name cannot exceed 50 characters' },
                    { pattern: /^[a-zA-Z][a-zA-Z\s]*[a-zA-Z]$/, message: 'Only letters and spaces allowed' },
                  ]}
                >
                  <Input prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter Full Name" size="large" onChange={handleNameInput} maxLength={50} />
                </Form.Item>

                <Form.Item label="Contact Method" style={{ marginBottom: 8 }}>
                  <Radio.Group onChange={handleContactMethodChange} value={contactMethod}>
                    <Radio value="email">Email</Radio>
                    <Radio value="mobile">Mobile</Radio>
                  </Radio.Group>
                </Form.Item>

                {contactMethod === 'email' ? (
                  <Form.Item name="email" label="Email Address" style={{ marginBottom: 12 }}
                    rules={[
                      { required: true, message: 'Please enter email address' },
                      { pattern: /^[a-zA-Z0-9._%-+]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: 'Invalid email address' },
                    ]}
                  >
                    <Input prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter Email Address" size="large" maxLength={100} />
                  </Form.Item>
                ) : (
                  <Form.Item label="Mobile Number" required style={{ marginBottom: 12 }}>
                    <PhoneInput
                      country={'in'}
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      inputStyle={{ width: '100%', height: 40, fontSize: 14, borderRadius: 6, paddingLeft: 52, border: '1px solid #d9d9d9' }}
                      buttonStyle={{ border: '1px solid #d9d9d9', borderRight: 'none', borderRadius: '6px 0 0 6px', backgroundColor: '#fafafa', width: 44 }}
                      dropdownStyle={{ width: 320, maxHeight: 260, overflowY: 'auto', zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', borderRadius: 8 }}
                      searchStyle={{ width: '100%', padding: '6px 10px', fontSize: 13 }}
                      containerStyle={{ width: '100%', position: 'relative' }}
                      enableSearch searchPlaceholder="Search country..." countryCodeEditable={false}
                      preferredCountries={['in', 'us', 'gb', 'ae', 'sg']}
                    />
                  </Form.Item>
                )}
                <Form.Item name="mobile" hidden />
                <Form.Item name="countrycode" hidden initialValue="+91" />

                <Form.Item name="password" style={{ marginBottom: 12 }}
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <span>Password</span>
                      <Button type="link" size="small" onClick={handleGeneratePassword} style={{ padding: 0, height: 'auto', fontSize: 12 }}>Generate Strong Password</Button>
                    </div>
                  }
                  rules={[
                    { required: true, message: 'Please enter a password' },
                    { min: 8, message: 'At least 8 characters' },
                    { pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/, message: 'Needs uppercase, lowercase, number & special character' },
                  ]}
                >
                  <Input.Password prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter Password" size="large" visibilityToggle={{ visible: showPassword, onVisibleChange: setShowPassword }} />
                </Form.Item>

                <Form.Item name="confirmPassword" label="Confirm Password" style={{ marginBottom: 0 }}
                  dependencies={['password']}
                  rules={[
                    { required: true, message: 'Please confirm the password' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) return Promise.resolve();
                        return Promise.reject(new Error('Passwords do not match'));
                      },
                    }),
                  ]}
                >
                  <Input.Password prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} placeholder="Confirm Password" size="large" />
                </Form.Item>
              </Col>

              {/* Right column: role + simplified access */}
              <Col xs={24} md={12}>
                {/* Role selector with info popover */}
                <Form.Item
                  name="role"
                  label={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Role
                      <Form.Item noStyle shouldUpdate={(p, c) => p.role !== c.role}>
                        {({ getFieldValue }) => {
                          const roleId = getFieldValue('role');
                          const roleInfo = roleOptions.find(r => r.value === roleId);
                          const desc = roleInfo ? ROLE_DESCRIPTIONS[roleInfo.label] : null;
                          if (!desc) return null;
                          const popContent = (
                            <div style={{ maxWidth: 280 }}>
                              <div style={{ fontSize: 13, color: '#555', marginBottom: 10, lineHeight: 1.5 }}>{desc.summary}</div>
                              <Divider style={{ margin: '8px 0' }} />
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Page Access</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {desc.capabilities.map(cap => (
                                  <div key={cap.page} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <span style={{ fontSize: 12, color: '#333' }}>
                                      {cap.perm === 'write'
                                        ? <UnlockFilled style={{ color: '#52c41a', marginRight: 5, fontSize: 11 }} />
                                        : <LockFilled style={{ color: '#faad14', marginRight: 5, fontSize: 11 }} />}
                                      {cap.page}
                                    </span>
                                    <Tag
                                      color={cap.perm === 'write' ? 'blue' : 'default'}
                                      style={{ margin: 0, fontSize: 10, padding: '0 5px' }}
                                    >
                                      {cap.perm === 'write' ? 'Read / Write' : 'Read Only'}
                                    </Tag>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                          return (
                            <Popover
                              content={popContent}
                              title={<span style={{ fontWeight: 600 }}>{roleInfo.label} — Permissions</span>}
                              trigger="click"
                              placement="rightTop"
                            >
                              <Button
                                type="text"
                                size="small"
                                icon={<InfoCircleOutlined style={{ color: '#1890ff', fontSize: 14 }} />}
                                style={{ padding: '0 2px', height: 'auto', lineHeight: 1 }}
                              />
                            </Popover>
                          );
                        }}
                      </Form.Item>
                    </span>
                  }
                  style={{ marginBottom: 12 }}
                  rules={[{ required: true, message: 'Please select a role' }]}
                >
                  <Select placeholder="Select Role" size="large" loading={rolesLoading}
                    suffixIcon={<SafetyOutlined style={{ color: '#bfbfbf' }} />}
                    onChange={handleRoleChange}
                  >
                    {roleOptions.map(role => (
                      <Option key={role.value} value={role.value}>
                        <Tag color={role.color} style={{ marginRight: 6 }}>{role.label}</Tag>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Role description card — shown once a role is selected */}
                <Form.Item noStyle shouldUpdate={(p, c) => p.role !== c.role}>
                  {({ getFieldValue }) => {
                    const roleId = getFieldValue('role');
                    const roleInfo = roleOptions.find(r => r.value === roleId);
                    const desc = roleInfo ? ROLE_DESCRIPTIONS[roleInfo.label] : null;
                    if (!roleInfo) return null;
                    return (
                      <div style={{
                        background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f4ff 100%)',
                        border: '1px solid #91caff',
                        borderRadius: 10,
                        padding: '10px 14px',
                        marginBottom: 14,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: desc ? 6 : 0 }}>
                          <Tag color={roleInfo.color} style={{ margin: 0, fontWeight: 600, fontSize: 12 }}>{roleInfo.label}</Tag>
                          <span style={{ fontSize: 11, color: '#1677ff', fontWeight: 500 }}>
                            {selectedPages.length} page{selectedPages.length !== 1 ? 's' : ''} assigned
                          </span>
                        </div>
                        {desc && (
                          <p style={{ fontSize: 12, color: '#4a6fa5', margin: 0, lineHeight: 1.5 }}>{desc.summary}</p>
                        )}
                        {!desc && (
                          <p style={{ fontSize: 12, color: '#888', margin: 0 }}>Default permissions applied for this role.</p>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Advanced Access toggle */}
                <div style={{ marginBottom: showAdvancedAccess ? 10 : 0 }}>
                  <Button
                    type="dashed"
                    size="small"
                    icon={showAdvancedAccess ? <UpOutlined /> : <DownOutlined />}
                    onClick={() => setShowAdvancedAccess(v => !v)}
                    style={{
                      borderRadius: 6,
                      fontSize: 12,
                      color: '#595959',
                      borderColor: '#d9d9d9',
                      width: '100%',
                      marginBottom: showAdvancedAccess ? 10 : 0,
                    }}
                  >
                    {showAdvancedAccess ? 'Hide Advanced Access' : 'Advanced Access'}
                  </Button>

                  {/* Page Access — hidden until Advanced Access is toggled */}
                  {showAdvancedAccess && (
                    <Form.Item label={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#434343' }}>Customize Page Permissions</span>
                        <Button size="small" type="link" style={{ padding: 0, height: 'auto', fontSize: 11 }}
                          onClick={() => {
                            if (selectedPages.length === AVAILABLE_PAGES.length) {
                              setSelectedPages([]);
                              setPagePermissions({});
                            } else {
                              const allKeys = AVAILABLE_PAGES.map(p => p.key);
                              setSelectedPages(allKeys);
                              const perms = {};
                              allKeys.forEach(k => { perms[k] = pagePermissions[k] || 'read'; });
                              setPagePermissions(perms);
                            }
                          }}
                        >
                          {selectedPages.length === AVAILABLE_PAGES.length ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                    } style={{ marginBottom: 0 }}>
                      <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden' }}>
                        {Object.entries(pageGroups).map(([groupName, pages]) => (
                          <div key={groupName}>
                            <div style={{ background: '#f5f5f5', padding: '6px 12px', borderBottom: '1px solid #e8e8e8', borderTop: '1px solid #e8e8e8' }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {groupName}
                              </span>
                            </div>
                            {pages.map((page, idx) => (
                              <div key={page.key} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '5px 12px',
                                background: selectedPages.includes(page.key) ? '#e6f4ff' : '#fff',
                                borderBottom: idx < pages.length - 1 ? '1px solid #f0f0f0' : 'none',
                                transition: 'background 0.15s',
                              }}>
                                <Checkbox
                                  checked={selectedPages.includes(page.key)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedPages(prev => [...prev, page.key]);
                                      setPagePermissions(prev => ({ ...prev, [page.key]: prev[page.key] || 'read' }));
                                    } else {
                                      setSelectedPages(prev => prev.filter(k => k !== page.key));
                                      setPagePermissions(prev => { const c = { ...prev }; delete c[page.key]; return c; });
                                    }
                                  }}
                                >
                                  <span style={{ fontSize: 12, fontWeight: selectedPages.includes(page.key) ? 600 : 400 }}>
                                    {page.icon} {page.label}
                                  </span>
                                </Checkbox>
                                {selectedPages.includes(page.key) && (
                                  <Select size="small" value={pagePermissions[page.key] || 'read'}
                                    onChange={(val) => setPagePermissions(prev => ({ ...prev, [page.key]: val }))}
                                    style={{ width: 95, flexShrink: 0 }} popupMatchSelectWidth={false}
                                  >
                                    <Option value="read"><Tag color="default" style={{ margin: 0, fontSize: 10 }}>Read</Tag></Option>
                                    <Option value="write"><Tag color="blue" style={{ margin: 0, fontSize: 10 }}>R/W</Tag></Option>
                                  </Select>
                                )}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </Form.Item>
                  )}
                </div>
              </Col>
            </Row>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
              <Button onClick={handleCloseCreateModal} size="large" style={{ borderRadius: 8 }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading} icon={<UserAddOutlined />}
                size="large" style={{ fontWeight: 600, borderRadius: 8, minWidth: 160 }}
              >
                Add Team Member
              </Button>
            </div>
          </Form>
        </Modal>
      )}

      {/* ── Credentials Modal ─────────────────────────────────────────────── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {createdCredentials?.restored
              ? <RollbackOutlined style={{ color: '#722ed1', fontSize: 24 }} />
              : <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
            }
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              {createdCredentials?.restored ? 'Team Member Restored!' : 'Team Member Created!'}
            </span>
            {createdCredentials?.restored && (
              <Tag color="purple" style={{ fontSize: 11, marginLeft: 4 }}>Restored</Tag>
            )}
          </div>
        }
        open={showCredentialsModal}
        onCancel={() => setShowCredentialsModal(false)}
        footer={[
          <Button key="copy-all" type="primary" icon={<CopyOutlined />}
            onClick={() => {
              const credentials = `
Team Member Credentials
-----------------------
Name: ${createdCredentials?.name}
Username: ${createdCredentials?.username}
Password: ${createdCredentials?.password}
Role: ${createdCredentials?.rolename}
              `.trim();
              copyToClipboard(credentials, 'All credentials');
            }}
          >Copy All</Button>,
          <Button key="close" onClick={() => setShowCredentialsModal(false)}>Close</Button>,
        ]}
        width={windowWidth < 576 ? '95%' : 500}
        centered
      >
        <Alert type="warning" showIcon
          message="Save these credentials!"
          description="Copy and securely share these with the team member. The password won't be recoverable later."
          style={{ marginBottom: 20 }}
        />
        <div style={{ background: '#f5f5f5', borderRadius: 12, padding: 20, border: '1px solid #e8e8e8' }}>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Full Name</Text>
            <Text strong style={{ fontSize: 16 }}>{createdCredentials?.name}</Text>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Username (Login ID)</Text>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', padding: '10px 14px', borderRadius: 8, border: '1px solid #d9d9d9', gap: 8 }}>
              <Text strong style={{ fontSize: 15, fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{createdCredentials?.username}</Text>
              <Button type="text" icon={<CopyOutlined />} onClick={() => copyToClipboard(createdCredentials?.username, 'Username')} size="small" />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Password</Text>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', padding: '10px 14px', borderRadius: 8, border: '1px solid #d9d9d9', gap: 8 }}>
              <Text strong style={{ fontSize: 15, fontFamily: 'monospace', color: '#1890ff', wordBreak: 'break-all', flex: 1 }}>{createdCredentials?.password}</Text>
              <Button type="text" icon={<CopyOutlined />} onClick={() => copyToClipboard(createdCredentials?.password, 'Password')} size="small" />
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Assigned Role</Text>
            <Tag color={roleColorMap[createdCredentials?.rolename] || 'default'} style={{ fontSize: 14, padding: '4px 12px' }}>
              {createdCredentials?.rolename}
            </Tag>
          </div>
          {createdCredentials?.pages?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Pages</Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {createdCredentials.pages.map(p => {
                  const perm = createdCredentials.permissions?.[p];
                  return (
                    <Tag key={p} color={perm === 'write' ? 'blue' : 'default'}>
                      {AVAILABLE_PAGES.find(ap => ap.key === p)?.label || p}
                      {perm === 'write' ? ' (R/W)' : ' (R)'}
                    </Tag>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div style={{ marginTop: 16, padding: 12, background: '#e6f7ff', borderRadius: 8 }}>
          <Text style={{ fontSize: 13, color: '#1890ff' }}>
            <KeyOutlined style={{ marginRight: 8 }} />
            The team member can log in using these credentials. We recommend they change their password after first login.
          </Text>
        </div>
      </Modal>

      {/* ── Edit Permissions Modal ────────────────────────────────────────── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EditOutlined style={{ color: '#1890ff' }} />
            <span>Edit Permissions — {editPermUser?.name || 'Member'}</span>
          </div>
        }
        open={!!editPermUser}
        onCancel={() => { setEditPermUser(null); setEditPermPages([]); setEditPermPerms({}); setEditPermRole(null); }}
        onOk={handleSavePermissions}
        confirmLoading={editPermLoading}
        okText="Save Permissions"
        width={windowWidth < 576 ? '92%' : windowWidth < 992 ? 600 : 700}
        bodyStyle={{ overflow: 'visible', padding: windowWidth < 576 ? '12px 14px 6px' : '14px 18px 8px' }}
        wrapClassName="admin-user-details-modal"
        style={{ top: windowWidth < 576 ? 18 : 30 }}
        destroyOnClose
      >
        {editPermUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: '#f6f6f6', padding: '10px 14px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                background: `linear-gradient(135deg, ${roleColorMap[editPermUser.rolename] || '#1890ff'}, #4096ff)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: 16,
              }}>
                {editPermUser.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{editPermUser.name || '—'}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{editPermUser.email || editPermUser.mobile || ''}</Text>
              </div>
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>Role</Text>
              <Select value={editPermRole} onChange={handleEditPermRoleChange} style={{ width: '100%' }} placeholder="Select role" loading={rolesLoading} size="middle">
                {roleOptions.map(role => (
                  <Option key={role.value} value={role.value}>{role.label}</Option>
                ))}
              </Select>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ fontSize: 13 }}>Page Access</Text>
                <Button size="small" type="link"
                  onClick={() => {
                    if (editPermPages.length === AVAILABLE_PAGES.length) {
                      setEditPermPages([]);
                      setEditPermPerms({});
                    } else {
                      const allKeys = AVAILABLE_PAGES.map(p => p.key);
                      setEditPermPages(allKeys);
                      const perms = {};
                      allKeys.forEach(k => { perms[k] = editPermPerms[k] || 'read'; });
                      setEditPermPerms(perms);
                    }
                  }}
                >
                  {editPermPages.length === AVAILABLE_PAGES.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden' }}>
                {Object.entries(pageGroups).map(([groupName, pages]) => (
                  <div key={groupName}>
                    <div style={{ background: '#f5f5f5', padding: '6px 12px', borderBottom: '1px solid #e8e8e8', borderTop: '1px solid #e8e8e8' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {groupName}
                      </span>
                    </div>
                    <div style={{
                      display: 'grid', gridTemplateColumns: windowWidth < 576 ? '1fr' : '1fr 1fr', gap: 1, background: '#e8e8e8',
                    }}>
                      {pages.map(page => {
                        const isChecked = editPermPages.includes(page.key);
                        return (
                          <div key={page.key} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '8px 12px', background: isChecked ? '#e6f4ff' : '#fff', gap: 8, transition: 'background 0.15s',
                          }}>
                            <Checkbox checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setEditPermPages(prev => [...prev, page.key]);
                                  setEditPermPerms(prev => ({ ...prev, [page.key]: prev[page.key] || 'read' }));
                                } else {
                                  setEditPermPages(prev => prev.filter(k => k !== page.key));
                                  setEditPermPerms(prev => { const copy = { ...prev }; delete copy[page.key]; return copy; });
                                }
                              }}
                              style={{ fontSize: 13 }}
                            >
                              <span style={{ fontSize: 12, fontWeight: isChecked ? 600 : 400 }}>{page.icon} {page.label}</span>
                            </Checkbox>
                            {isChecked && (
                              <Select size="small" value={editPermPerms[page.key] || 'read'}
                                onChange={(val) => setEditPermPerms(prev => ({ ...prev, [page.key]: val }))}
                                style={{ width: 85, flexShrink: 0 }} popupMatchSelectWidth={false}
                              >
                                <Option value="read">Read</Option>
                                <Option value="write">R/W</Option>
                              </Select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Restore Deactivated Team Member Modal ─────────────────────── */}
      <Modal
        open={restoreModal.visible}
        onCancel={() => !restoreModal.loading && setRestoreModal({ visible: false, users: [], pendingFormData: null, loading: false })}
        footer={null}
        width={480}
        centered
        closable={!restoreModal.loading}
        maskClosable={!restoreModal.loading}
        styles={{ body: { padding: 0 } }}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #fff7e6 0%, #fff1b8 100%)',
          borderBottom: '1px solid #ffe58f',
          padding: '24px 24px 20px',
          borderRadius: '8px 8px 0 0',
          display: 'flex', alignItems: 'flex-start', gap: 14,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'linear-gradient(135deg, #fa8c16, #faad14)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, color: '#fff', flexShrink: 0, boxShadow: '0 4px 12px rgba(250,140,22,0.3)',
          }}>
            <HistoryOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#222', marginBottom: 4 }}>Previously Deactivated Member Found</div>
            <div style={{ fontSize: 13, color: '#8c6914', lineHeight: 1.5 }}>
              A deactivated account with the same credentials was found.<br />
              You can restore it or use different credentials.
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {restoreModal.users.map((user, idx) => (
            <div key={idx} style={{
              border: '1px solid #e8e8e8', borderRadius: 12, overflow: 'hidden',
              marginBottom: idx < restoreModal.users.length - 1 ? 12 : 0,
            }}>
              {/* User card header */}
              <div style={{
                background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f4ff 100%)',
                padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
                borderBottom: '1px solid #e8e8e8',
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #531dab, #722ed1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0,
                }}>
                  {(user.companyname || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#222', marginBottom: 1 }}>{user.companyname}</div>
                  <Tag color="orange" style={{ marginTop: 2, fontSize: 11 }}>Deactivated</Tag>
                </div>
              </div>
              {/* Details */}
              <div style={{ padding: '12px 16px', background: '#fff' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 0' }}>
                  {user.emailid && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <MailOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                      <Text style={{ fontSize: 12 }} copyable={{ text: user.emailid }}>{user.emailid}</Text>
                    </div>
                  )}
                  {user.contactno && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <PhoneOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                      <Text style={{ fontSize: 12 }}>{user.countrycode ? `+${user.countrycode.replace('+', '')}` : ''}{user.contactno}</Text>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <SafetyOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                    <Text style={{ fontSize: 12 }}>{user.company_type || user.rolename || '—'}</Text>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* What will be updated */}
          {restoreModal.pendingFormData && (
            <div style={{
              marginTop: 16, padding: '12px 14px',
              background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8,
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 12, color: '#237804', lineHeight: 1.7 }}>
                <strong>Restoring will update: </strong>
                name to <strong>&ldquo;{restoreModal.pendingFormData.companyname}&rdquo;</strong>,
                set a new password, update role &amp; page access.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex', gap: 10, justifyContent: 'flex-end',
          background: '#fafafa', borderRadius: '0 0 8px 8px',
        }}>
          <Button
            onClick={() => setRestoreModal({ visible: false, users: [], pendingFormData: null, loading: false })}
            disabled={restoreModal.loading}
            size="large"
          >
            Cancel — Use Different Credentials
          </Button>
          <Button
            type="primary"
            icon={<RollbackOutlined />}
            loading={restoreModal.loading}
            onClick={handleRestore}
            size="large"
            style={{ background: 'linear-gradient(135deg, #531dab, #722ed1)', border: 'none' }}
          >
            Restore This Member
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ClientAdminManagement;
